import { useState, useEffect, useContext } from 'react';
import { ApiContext } from '../../App';
import { Calendar, ExternalLink, AlertCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { getAllEmails } from '../../utils/emailHelpers';

const CalendlyEventsCard = ({ individuals = [] }) => {
  const API = useContext(ApiContext);
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [calendlyEmail, setCalendlyEmail] = useState('');
  const [error, setError] = useState(null);
  const [loadingInvitees, setLoadingInvitees] = useState({});

  const fetchCalendlyEvents = async () => {
    try {
      // Récupérer uniquement le statut Calendly
      const statusResponse = await API.get('/api/calendly/status');

      setIsConnected(statusResponse.data.isConnected);
      setCalendlyEmail(statusResponse.data.email || '');

      console.log('Calendly Status:', statusResponse.data);

      if (statusResponse.data.isConnected) {
        try {
          // Récupérer les événements
          const eventsResponse = await API.get('/api/calendly/events');

          console.log('Calendly Events Response:', eventsResponse.data);

          // Vérifier si la réponse contient des données
          if (eventsResponse.data && eventsResponse.data.collection) {
            // Filtrer et trier les événements à venir uniquement
            const upcomingEvents = eventsResponse.data.collection
              .filter(event => new Date(event.start_time) > new Date())
              .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
              .slice(0, 5); // Limiter à 5 événements pour réduire les appels API

            console.log('Upcoming events:', upcomingEvents);

            // Afficher les événements immédiatement avec un état de chargement pour les invités
            const eventsWithLoadingState = upcomingEvents.map(event => ({
              ...event,
              invitees: null // null signifie "en cours de chargement"
            }));
            setEvents(eventsWithLoadingState);
            setLoading(false);

            // Marquer tous les événements comme en chargement
            const loadingState = {};
            upcomingEvents.forEach((event, index) => {
              loadingState[index] = true;
            });
            setLoadingInvitees(loadingState);

            // Charger les invités en arrière-plan pour chaque événement
            upcomingEvents.forEach(async (event, index) => {
              try {
                const eventUuid = event.uri.split('/').pop();
                const inviteesResponse = await API.get(`/api/calendly/events/${eventUuid}/invitees`);

                // Mettre à jour l'événement spécifique avec ses invités
                setEvents(prevEvents => {
                  const newEvents = [...prevEvents];
                  newEvents[index] = {
                    ...newEvents[index],
                    invitees: inviteesResponse.data.collection || []
                  };
                  return newEvents;
                });

                // Marquer comme chargé
                setLoadingInvitees(prev => ({
                  ...prev,
                  [index]: false
                }));
              } catch (err) {
                console.error(`Error fetching invitees for event ${event.uri}:`, err);
                // En cas d'erreur, mettre un tableau vide
                setEvents(prevEvents => {
                  const newEvents = [...prevEvents];
                  newEvents[index] = {
                    ...newEvents[index],
                    invitees: []
                  };
                  return newEvents;
                });
                setLoadingInvitees(prev => ({
                  ...prev,
                  [index]: false
                }));
              }
            });
          } else {
            console.warn('No collection in response:', eventsResponse.data);
            setEvents([]);
            setLoading(false);
          }
        } catch (eventsErr) {
          console.error('Error fetching events:', eventsErr);

          // Si le backend demande une reconnexion
          if (eventsErr.response?.data?.requireReconnect) {
            setIsConnected(false);
            setError('Votre session Calendly a expiré. Veuillez vous reconnecter.');
          } else {
            setError(eventsErr.response?.data?.error || eventsErr.message);
          }
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching Calendly events:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    // Charger immédiatement au lieu d'attendre 500ms
    fetchCalendlyEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDateTime = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const dateStr = start.toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
    const timeStr = `${start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    return `${dateStr} • ${timeStr}`;
  };

  const findIndividualByInvitee = (invitee) => {
    if (!invitee || !individuals.length) return null;

    // Rechercher par email
    let found = individuals.find(ind => {
      const professionalEmails = getAllEmails(ind.professionalEmail);
      const personalEmails = getAllEmails(ind.personalEmail);

      return (
        professionalEmails.some(email => email.toLowerCase() === invitee.email?.toLowerCase()) ||
        personalEmails.some(email => email.toLowerCase() === invitee.email?.toLowerCase())
      );
    });

    // Si pas trouvé par email, rechercher par nom
    if (!found && invitee.name) {
      found = individuals.find(ind =>
        ind.Name && ind.Name.toLowerCase() === invitee.name.toLowerCase()
      );
    }

    return found;
  };

  const handleIndividualClick = (individualId) => {
    navigate(`/app/contacts/${individualId}`);
  };

  const getInitials = (fullName) => {
    if (!fullName) return '?';
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Calendar className="text-blue-600" size={20} />
            <h3 className="text-lg font-semibold my-0">Rendez-vous à venir</h3>
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Calendar className="text-blue-600" size={20} />
            <h3 className="text-lg font-semibold my-0">Rendez-vous à venir</h3>
          </div>
        </div>
        <div className="text-center py-8">
          <AlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-3" />
          <p className="text-sm text-gray-600 mb-4">
            Connectez votre compte Calendly pour voir vos rendez-vous à venir
          </p>
          <Link
            to="/settings"
            className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
          >
            <span>Connecter Calendly</span>
            <ExternalLink size={14} />
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Calendar className="text-blue-600" size={20} />
            <h3 className="text-lg font-semibold my-0">Rendez-vous à venir</h3>
          </div>
        </div>
        <div className="text-center py-8">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-3" />
          <p className="text-sm text-red-600">
            Erreur lors du chargement des événements
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Calendar className="text-blue-600" size={20} />
          <h3 className="text-lg font-semibold my-0 ">Rendez-vous à venir</h3>
        </div>
        <span className="text-xs text-gray-500">
          {events.length} événement{events.length > 1 ? 's' : ''}
        </span>
      </div>

      {events.length === 0 ? (
        <div className="text-center py-8">
          <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-3" />
          <p className="text-sm text-gray-600">
            Aucun rendez-vous à venir
          </p>
        </div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
          {events.map((event, eventIndex) => {
            const invitee = event.invitees && event.invitees.length > 0 ? event.invitees[0] : null;
            const individual = invitee ? findIndividualByInvitee(invitee) : null;
            const isLoadingInvitee = event.invitees === null; // null = en cours de chargement

            return (
            <div
              key={event.uri}
              className="p-2.5 border rounded-lg hover:bg-gray-50 transition-colors"
            >
              {/* Première ligne : Nom de l'événement (cliquable) + Invité avec photo */}
              <div className="flex items-center gap-3 mb-2">
                {/* Event name avec icône de redirection */}
                <div className="flex items-center space-x-2 min-w-0 flex-shrink">
                  {event.location && event.location.join_url ? (
                    <a
                      href={event.location.join_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-1.5 text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors truncate group"
                    >
                      <span className="truncate">{event.name}</span>
                      <ExternalLink size={14} className="flex-shrink-0 text-gray-400 group-hover:text-blue-600" />
                    </a>
                  ) : (
                    <h4 className="text-sm font-medium text-gray-900 truncate m-0">
                      {event.name}
                    </h4>
                  )}
                </div>

                {/* Photo de l'invité si trouvé dans le CRM */}
                {isLoadingInvitee ? (
                  // Skeleton pendant le chargement
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-auto">
                    <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse flex-shrink-0"></div>
                    <div className='flex flex-col space-y-1'>
                      <div className="h-3 w-20 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-2.5 w-24 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                ) : invitee ? (
                  // Invité chargé
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-auto">
                    {individual ? (
                      <div
                        onClick={() => handleIndividualClick(individual._id)}
                        className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
                        title={`Voir le profil de ${individual.Name}`}
                      >
                        {individual.photo ? (
                          <img
                            src={individual.photo}
                            alt={individual.Name}
                            className="w-8 h-8 rounded-full object-cover border-2 border-blue-500 flex-shrink-0"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-medium border-2 border-blue-500 flex-shrink-0">
                            {getInitials(individual.Name)}
                          </div>
                        )}
                        <div className='flex flex-col'>
                          <span className="text-xs font-medium text-gray-700 whitespace-nowrap">
                            {individual.Name}
                          </span>
                          <span className='text-xs text-gray-600 whitespace-nowrap'>
                            {invitee.email}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2" title={invitee.email}>
                        <div className="w-8 h-8 rounded-full bg-gray-300 text-white flex items-center justify-center text-xs font-medium flex-shrink-0">
                          {getInitials(invitee.name)}
                        </div>
                        <div className='flex flex-col'>
                          <span className="text-xs font-medium text-gray-700 whitespace-nowrap">
                            {invitee.name}
                          </span>
                          <span className='text-xs text-gray-600 whitespace-nowrap'>
                            {invitee.email}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              {/* Deuxième ligne : Date et heure combinées */}
              <div className="flex items-center space-x-1 text-xs text-gray-600">
                <Calendar size={12} className="text-blue-500 flex-shrink-0" />
                <span>{formatDateTime(event.start_time, event.end_time)}</span>
              </div>

              {/* Afficher la localisation physique si présente */}
              {event.location && event.location.type === 'physical' && event.location.location && (
                <div className="mt-1.5 text-xs text-gray-500 flex items-center space-x-1">
                  <span>📍</span>
                  <span className="truncate">{event.location.location}</span>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CalendlyEventsCard;
